import { Component, ElementRef, EventEmitter, Input, Output, forwardRef, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  ConnectorVariableScope,
  VariableParserSuggestion,
} from '../../../apps/integrations/intergration.types';

type VariableParserSuggestionItem = VariableParserSuggestion & {
  createNew?: boolean;
};

interface ActiveVariableToken {
  open: number;
  cursor: number;
  query: string;
  scope: ConnectorVariableScope;
}

@Component({
  selector: 'app-variable-parser-input',
  templateUrl: './variable-parser-input.html',
  standalone: false,
  styleUrl: './variable-parser-input.scss',
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => VariableParserInputComponent),
    multi: true
  }]
})
export class VariableParserInputComponent implements ControlValueAccessor {

  @Input() suggestions: Array<string | VariableParserSuggestion> = [];
  @Input() allowCreate = true;
  @Input() forceScope: ConnectorVariableScope | null = null;
  @Input() placeholder = "Enter value, e.g. {{variable_name}}";
  @Input() readonly = false;
  @Input() disabled = false;
  @Input() inputClass = 'form-control';
  @Input() inputType: 'text' | 'password' | 'url' | 'search' = 'text';
  @Output() variableCreated = new EventEmitter<VariableParserSuggestion>();
  @Output() variableSelected = new EventEmitter<VariableParserSuggestion>();
  @Output() variableConflict = new EventEmitter<string>();

  @ViewChild('inputRef') input!: ElementRef<HTMLInputElement>;

  value = '';
  filtered: VariableParserSuggestionItem[] = [];
  showSuggestions = false;
  private readonly defaultSuggestions: VariableParserSuggestion[] = [
    { key: 'name', scope: 'local' },
    { key: 'email', scope: 'local' },
    { key: 'phone', scope: 'local' },
    { key: 'created_at', scope: 'local' },
  ];

  private onChange = (_: any) => { };
  onTouched = () => { };

  writeValue(val: string) {
    this.value = val || '';

    if (this.input) {
      this.input.nativeElement.value = this.value;
    }
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // ===== INPUT CONTROL =====

  onKeyDown(e: KeyboardEvent) {
    if (this.readonly || this.disabled) {
      return;
    }

    if (e.key === '{' || e.key === '[') {
      const input = e.target as HTMLInputElement;

      setTimeout(() => {
        const cursor = input.selectionStart ?? input.value.length;
        const before = input.value.slice(0, cursor);
        const after = input.value.slice(cursor);

        if (before.endsWith('{{') && !after.startsWith('}}')) {
          const nextValue = `${before}}}${after}`;
          this.value = nextValue;
          input.value = this.value;

          this.updateValue();
          this.moveCursor(cursor);
          this.handleSuggestions(input);
        }

        if (before.endsWith('[[') && !after.startsWith(']]')) {
          const nextValue = `${before}]]${after}`;
          this.value = nextValue;
          input.value = this.value;

          this.updateValue();
          this.moveCursor(cursor);
          this.handleSuggestions(input);
        }
      });
    }
  }

  onInput(e: Event) {
    if (this.readonly || this.disabled) {
      return;
    }

    const input = e.target as HTMLInputElement;
    this.value = input.value;
    this.updateValue();
    this.handleSuggestions(input);
  }

  handleSuggestions(input: HTMLInputElement) {
    if (this.readonly || this.disabled) {
      this.filtered = [];
      this.showSuggestions = false;
      return;
    }

    const token = this.activeToken(input);
    if (!token) {
      this.filtered = [];
      this.showSuggestions = false;
      return;
    }

    const rawQuery = token.query.toLowerCase();
    const available = this.availableSuggestions;
    const filtered: VariableParserSuggestionItem[] = available.filter((suggestion) => {
      const normalized = suggestion.key.toLowerCase();

      if (!rawQuery) {
        return true;
      }

      return normalized.includes(rawQuery)
        || String(suggestion.label ?? '').toLowerCase().includes(rawQuery);
    });

    if (
      this.allowCreate
      && rawQuery
      && !available.some((suggestion) => suggestion.key.toLowerCase() === rawQuery)
    ) {
      filtered.push({
        key: rawQuery,
        label: `Create ${rawQuery}`,
        scope: this.forceScope ?? token.scope,
        createNew: true,
      });
    }

    this.showSuggestions = this.filtered.length > 0;
    this.filtered = filtered;
    this.showSuggestions = filtered.length > 0;
  }

  onBlur(): void {
    window.setTimeout(() => {
      this.showSuggestions = false;
      this.onTouched();
    }, 120);
  }

  onSuggestionMouseDown(event: MouseEvent): void {
    event.preventDefault();
  }

  onSuggestionsOpenChange(open: boolean): void {
    this.showSuggestions = open;
  }

  select(s: VariableParserSuggestionItem) {
    const cursor = this.input?.nativeElement.selectionStart ?? this.value.length;
    const before = this.value.slice(0, cursor);
    const after = this.value.slice(cursor);
    const token = this.activeToken(this.input?.nativeElement);
    const selectedScope = this.forceScope ?? s.scope ?? token?.scope ?? 'local';
    const key = this.normalizeKey(s.key);
    const variableToken = this.formatToken(key, selectedScope);

    if (token && token.scope !== selectedScope) {
      this.variableConflict.emit(
        `Variable "${key}" is ${selectedScope}, so the braces were changed.`,
      );
    }

    if (this.forceScope && token && token.scope !== this.forceScope) {
      this.variableConflict.emit(
        `This field only accepts ${this.forceScope} variables, so "${key}" was converted.`,
      );
    }

    if (!token) {
      this.value = variableToken;
      this.updateValue();
      this.showSuggestions = false;
      this.moveCursor(this.value.length);
      this.emitSelection(s, key, selectedScope);
      return;
    }

    const nextValue = `${before.slice(0, token.open)}${variableToken}${after}`;
    this.value = nextValue;
    this.showSuggestions = false;
    this.updateValue();
    this.moveCursor(token.open + variableToken.length);
    this.emitSelection(s, key, selectedScope);
  }

  private updateValue() {
    this.onChange(this.value);
  }

  private get availableSuggestions(): VariableParserSuggestion[] {
    const source = this.suggestions.length > 0 ? this.suggestions : this.defaultSuggestions;
    const unique = new Map<string, VariableParserSuggestion>();

    for (const suggestion of source) {
      const item = typeof suggestion === 'string'
        ? { key: suggestion, scope: 'local' as ConnectorVariableScope }
        : suggestion;
      const key = this.normalizeKey(item.key);

      if (key.length > 0) {
        unique.set(`${item.scope ?? 'local'}:${key}`, {
          ...item,
          key,
          scope: item.scope ?? 'local',
        });
      }
    }

    return [...unique.values()];
  }

  private activeToken(input: HTMLInputElement | undefined): ActiveVariableToken | null {
    if (!input) {
      return null;
    }

    const cursor = input.selectionStart ?? this.value.length;
    const before = this.value.slice(0, cursor);
    const localOpen = before.lastIndexOf('{{');
    const localClose = before.lastIndexOf('}}');
    const globalOpen = before.lastIndexOf('[[');
    const globalClose = before.lastIndexOf(']]');
    const localActive = localOpen > localClose;
    const globalActive = globalOpen > globalClose;

    if (!localActive && !globalActive) {
      return null;
    }

    const scope: ConnectorVariableScope = globalActive && globalOpen > localOpen ? 'global' : 'local';
    const open = scope === 'global' ? globalOpen : localOpen;

    return {
      open,
      cursor,
      scope,
      query: before.slice(open + 2).trim(),
    };
  }

  private formatToken(key: string, scope: ConnectorVariableScope): string {
    return scope === 'global' ? `[[${key}]]` : `{{${key}}}`;
  }

  private normalizeKey(value: string): string {
    return String(value ?? '')
      .trim()
      .replace(/^[{\[]+/, '')
      .replace(/[}\]]+$/, '')
      .trim()
      .toLowerCase();
  }

  private emitSelection(
    suggestion: VariableParserSuggestionItem,
    key: string,
    scope: ConnectorVariableScope,
  ): void {
    const event = {
      ...suggestion,
      key,
      scope,
    };

    if (suggestion.createNew) {
      this.variableCreated.emit(event);
      return;
    }

    this.variableSelected.emit(event);
  }

  private moveCursor(pos: number) {
    requestAnimationFrame(() => {
      this.input?.nativeElement.focus();
      this.input?.nativeElement.setSelectionRange(pos, pos);
    });
  }
}
